import { AlertDialogHeader } from '@/components/alert-dialog/alert-dialog'
import { Button } from '@/components/button/button'
import { Card } from '@/components/card/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/dialog/dialog'
import { Form, FormField } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { Label } from '@/components/label/label'

import { cn } from '@/lib/utils'

import { AddButton } from './add-button'
import { EmailChips } from './email-chips'
import { useInviteMemberDialog } from './use-invite-member-dialog'

export interface InviteMemberDialogProps {
  open: boolean
  onClose: VoidFunction
}

export const InviteMemberDialog = ({ open, onClose }: InviteMemberDialogProps) => {
  const {
    adminEmailForm,
    userEmailForm,
    userEmails,
    adminEmails,
    isDisabledInviteButton,
    isPendingMemberInviting,
    handleSubmitAdminInput,
    handleSubmitUserInput,
    handleClickAdminEmailChip,
    handleClickUserEmailChip,
    handleClickInviteButton,
  } = useInviteMemberDialog({ onClose })

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogDescription hidden>
        Invite members by email addresses. You can invite both administrators and regular users.
      </DialogDescription>
      <DialogContent
        onPointerDownOutside={(e) => {
          e.preventDefault()
        }}
      >
        <AlertDialogHeader className="mb-4">
          <DialogTitle>Invite Members</DialogTitle>
        </AlertDialogHeader>

        <div className={cn('space-y-4')}>
          <Card className={cn('p-6')}>
            <Form {...adminEmailForm}>
              <form onSubmit={adminEmailForm.handleSubmit(handleSubmitAdminInput)}>
                <FormField
                  control={adminEmailForm.control}
                  name="email"
                  render={({ field }) => (
                    <>
                      <Label htmlFor="admin-email">Invite Admin</Label>
                      <div className={cn('relative', 'mt-4')}>
                        <Input
                          id="admin-email"
                          className={cn('pr-[66px]')}
                          placeholder="Invite others by email"
                          errorMessage={
                            adminEmailForm.formState.errors.email ? 'Please input valid email address.' : ''
                          }
                          value={field.value}
                          onChange={field.onChange}
                        />
                        <div className={cn('absolute right-4 top-1.5')}>
                          <AddButton />
                        </div>
                      </div>
                    </>
                  )}
                />
              </form>
            </Form>
            {adminEmails.length > 0 && (
              <div className={cn('mt-4')}>
                <EmailChips values={adminEmails} onClickChip={handleClickAdminEmailChip} />
              </div>
            )}
          </Card>
          <Card className={cn('p-6')}>
            <Form {...userEmailForm}>
              <form onSubmit={userEmailForm.handleSubmit(handleSubmitUserInput)}>
                <FormField
                  control={userEmailForm.control}
                  name="email"
                  render={({ field }) => (
                    <>
                      <Label htmlFor="user-email">Invite User</Label>
                      <div className={cn('relative', 'mt-4')}>
                        <Input
                          id="user-email"
                          className={cn('pr-[66px]')}
                          placeholder="Invite others by email"
                          errorMessage={userEmailForm.formState.errors.email ? 'Please input valid email address.' : ''}
                          value={field.value}
                          onChange={field.onChange}
                        />
                        <div className={cn('absolute right-4 top-1.5')}>
                          <AddButton />
                        </div>
                      </div>
                    </>
                  )}
                />
              </form>
            </Form>
            {userEmails.length > 0 && (
              <div className={cn('mt-4')}>
                <EmailChips values={userEmails} onClickChip={handleClickUserEmailChip} />
              </div>
            )}
          </Card>
        </div>
        <Button loading={isPendingMemberInviting} disabled={isDisabledInviteButton} onClick={handleClickInviteButton}>
          Invite
        </Button>
      </DialogContent>
    </Dialog>
  )
}
